import { useAuthStore } from '@/stores/auth.store'

const CMS_BASE =
  (import.meta.env.VITE_CMS_SERVICE_URL as string | undefined) ?? 'http://localhost:8090'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CmsSettings {
  site_name: string
  logo_url?: string
  favicon_url?: string
  description?: string
  phone?: string
  email?: string
  address?: string
  facebook?: string
  linkedin?: string
  twitter?: string
  whatsapp?: string
  marquee_items?: string[]
  partner_stats?: { num: string; label: string }[]
  footer_about_fr?: string
  footer_about_en?: string
  footer_legal?: string
  hours_fr?: string
  hours_en?: string
}

export interface HeroSlide {
  id: string
  sort_order: number
  is_active: boolean
  bg_image_url?: string
  eyebrow_fr: string
  eyebrow_en: string
  title_line1_fr: string
  title_line1_en: string
  title_accent_fr: string
  title_accent_en: string
  description_fr: string
  description_en: string
  cta1_label_fr: string
  cta1_href: string
  cta2_label_fr?: string
  cta2_href?: string
}

export interface TickerItem {
  id: string
  sort_order: number
  is_active: boolean
  tag: string
  text_fr: string
  text_en: string
}

export interface ServiceStat {
  num: string
  label_fr: string
  label_en: string
}

export interface ServiceFeature {
  icon: string
  title_fr: string
  title_en: string
  desc_fr: string
  desc_en: string
}

export interface ServiceProcessStep {
  title_fr: string
  title_en: string
  desc_fr: string
  desc_en: string
}

export interface CmsService {
  id: string
  slug: string
  sort_order: number
  is_active: boolean
  icon_url?: string
  image_url?: string
  name: string
  name_en?: string
  description?: string
  description_en?: string
  title_accent_fr?: string
  title_accent_en?: string
  stats: ServiceStat[]
  features: ServiceFeature[]
  process: ServiceProcessStep[]
}

export interface KpiMetric {
  id: string
  sort_order: number
  is_active: boolean
  numeric_value: number
  suffix?: string
  label_fr: string
  label_en: string
}

export interface WhyItem {
  id: string
  sort_order: number
  number_label: string
  title_fr: string
  title_en: string
  description_fr: string
  description_en: string
}

export interface Partner {
  id: string
  sort_order: number
  name: string
  logo_url?: string
  website_url?: string
  is_active: boolean
}

export interface Testimonial {
  id: string
  name: string
  role?: string
  company?: string
  text_fr: string
  text_en: string
  stars: number
  avatar_url?: string
  is_published: boolean
}

export interface NewsCategory {
  id: string
  slug: string
  name: string
  description?: string
  is_active: boolean
  sort_order: number
  article_count?: number
}

/** Backend enum — NOT the news category, see `category_id` for that. */
export type ArticleType = 'article' | 'document' | 'flash' | 'magazine'

export interface Article {
  id: string
  slug: string
  category_id: string
  category?: { id: string; name: string; slug: string }
  article_type: ArticleType
  title_fr: string
  title_en?: string
  excerpt_fr?: string
  excerpt_en?: string
  content_fr?: string
  content_en?: string
  file_url?: string
  cover_image_url?: string
  author?: string
  read_time_min?: number
  is_published: boolean
  is_pinned: boolean
  published_at?: string
  created_at: string
  updated_at: string
}

export interface AboutValue {
  id: string
  icon: string
  title_fr: string
  title_en?: string
  description_fr: string
  description_en?: string
  sort_order: number
  is_active: boolean
}

export interface AboutTimelineItem {
  id: string
  year: string
  title_fr: string
  title_en?: string
  description_fr: string
  description_en?: string
  sort_order: number
  is_active: boolean
}

export interface Office {
  id: string
  city: string
  country: string
  role_fr: string
  role_en?: string
  address?: string
  phone?: string
  email?: string
  sort_order: number
  is_active: boolean
}

export interface GalleryItem {
  id: string
  url: string
  caption_fr?: string
  caption_en?: string
  alt_text?: string
  sort_order: number
  is_active: boolean
}

export interface NavItem {
  id: string
  location: string
  label_fr: string
  label_en: string
  href: string
  sort_order: number
  is_active: boolean
}

// ── Fetch helper ──────────────────────────────────────────────────────────────

async function cmsFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  if (options.body && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  const res = await fetch(`${CMS_BASE}${path}`, {
    ...options,
    headers: { ...headers, ...((options.headers as Record<string, string>) ?? {}) },
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'unknown' }))
    throw new Error(err.error ?? `HTTP ${res.status}`)
  }
  const json = await res.json()
  return (json.data ?? json) as T
}

// ── Public ────────────────────────────────────────────────────────────────────

const pub = '/api/v1/cms'

// ── Admin ─────────────────────────────────────────────────────────────────────

const adm = '/api/v1/admin/cms'

export const cmsApi = {
  // Settings
  getSettings: () => cmsFetch<CmsSettings>(`${pub}/settings`),
  updateSettings: (body: Partial<CmsSettings>) =>
    cmsFetch<CmsSettings>(`${adm}/settings`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Hero
  listHero: () => cmsFetch<HeroSlide[]>(`${pub}/hero`),

  // Ticker
  listTicker: () => cmsFetch<TickerItem[]>(`${pub}/ticker`),

  // Services
  listServices: () => cmsFetch<CmsService[]>(`${adm}/services`),
  updateService: (id: string, body: Partial<CmsService>) =>
    cmsFetch<CmsService>(`${adm}/services/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  reorderServices: (ids: string[]) =>
    cmsFetch<void>(`${adm}/services/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),

  // KPIs
  listKpis: () => cmsFetch<KpiMetric[]>(`${adm}/kpis`),
  updateKpi: (id: string, body: Partial<KpiMetric>) =>
    cmsFetch<KpiMetric>(`${adm}/kpis/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // Why Items
  listWhyItems: () => cmsFetch<WhyItem[]>(`${pub}/why-items`),

  // Partners
  listPartners: () => cmsFetch<Partner[]>(`${adm}/partners`),
  createPartner: (body: Omit<Partner, 'id'>) =>
    cmsFetch<Partner>(`${adm}/partners`, { method: 'POST', body: JSON.stringify(body) }),
  updatePartner: (id: string, body: Partial<Partner>) =>
    cmsFetch<Partner>(`${adm}/partners/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deletePartner: (id: string) => cmsFetch<void>(`${adm}/partners/${id}`, { method: 'DELETE' }),

  // Testimonials
  listTestimonials: () => cmsFetch<Testimonial[]>(`${adm}/testimonials`),
  createTestimonial: (body: Omit<Testimonial, 'id'>) =>
    cmsFetch<Testimonial>(`${adm}/testimonials`, { method: 'POST', body: JSON.stringify(body) }),
  updateTestimonial: (id: string, body: Partial<Testimonial>) =>
    cmsFetch<Testimonial>(`${adm}/testimonials/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteTestimonial: (id: string) =>
    cmsFetch<void>(`${adm}/testimonials/${id}`, { method: 'DELETE' }),
  togglePublishTestimonial: (id: string) =>
    cmsFetch<Testimonial>(`${adm}/testimonials/${id}/publish`, { method: 'POST' }),

  // Articles
  listArticles: (params?: {
    type?: string
    category?: string
    limit?: number
    cursor?: string
  }) => {
    const q = new URLSearchParams()
    if (params?.type) q.set('type', params.type)
    if (params?.category) q.set('category', params.category)
    if (params?.limit) q.set('limit', String(params.limit))
    if (params?.cursor) q.set('cursor', params.cursor)
    const qs = q.toString()
    return cmsFetch<{ articles: Article[]; next_cursor?: string }>(
      `${adm}/articles${qs ? `?${qs}` : ''}`,
    )
  },
  getArticle: (id: string) => cmsFetch<Article>(`${adm}/articles/${id}`),
  createArticle: (body: Partial<Article>) =>
    cmsFetch<Article>(`${adm}/articles`, { method: 'POST', body: JSON.stringify(body) }),
  updateArticle: (id: string, body: Partial<Article>) =>
    cmsFetch<Article>(`${adm}/articles/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteArticle: (id: string) => cmsFetch<void>(`${adm}/articles/${id}`, { method: 'DELETE' }),
  togglePublish: (id: string) =>
    cmsFetch<Article>(`${adm}/articles/${id}/publish`, { method: 'POST' }),
  togglePin: (id: string) => cmsFetch<Article>(`${adm}/articles/${id}/pin`, { method: 'POST' }),

  // Categories
  listCategories: () => cmsFetch<NewsCategory[]>(`${adm}/categories`),
  createCategory: (body: Omit<NewsCategory, 'id'>) =>
    cmsFetch<NewsCategory>(`${adm}/categories`, { method: 'POST', body: JSON.stringify(body) }),
  updateCategory: (id: string, body: Partial<NewsCategory>) =>
    cmsFetch<NewsCategory>(`${adm}/categories/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteCategory: (id: string) => cmsFetch<void>(`${adm}/categories/${id}`, { method: 'DELETE' }),

  // Gallery
  listGallery: () => cmsFetch<GalleryItem[]>(`${adm}/gallery`),
  updateGalleryItem: (id: string, body: Partial<GalleryItem>) =>
    cmsFetch<GalleryItem>(`${adm}/gallery/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteGalleryItem: (id: string) => cmsFetch<void>(`${adm}/gallery/${id}`, { method: 'DELETE' }),
  uploadMedia: (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    return cmsFetch<{ url: string }>(`${adm}/media/upload`, { method: 'POST', body: fd })
  },

  // Nav
  listNav: () => cmsFetch<NavItem[]>(`${adm}/nav`),
  createNav: (body: Omit<NavItem, 'id'>) =>
    cmsFetch<NavItem>(`${adm}/nav`, { method: 'POST', body: JSON.stringify(body) }),
  reorderNav: (ids: string[]) =>
    cmsFetch<void>(`${adm}/nav/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),

  // About — values
  listAboutValues: () => cmsFetch<AboutValue[]>(`${adm}/about-values`),
  createAboutValue: (body: Omit<AboutValue, 'id' | 'is_active'>) =>
    cmsFetch<AboutValue>(`${adm}/about-values`, { method: 'POST', body: JSON.stringify(body) }),
  updateAboutValue: (id: string, body: Partial<AboutValue>) =>
    cmsFetch<AboutValue>(`${adm}/about-values/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAboutValue: (id: string) =>
    cmsFetch<void>(`${adm}/about-values/${id}`, { method: 'DELETE' }),

  // About — timeline
  listAboutTimeline: () => cmsFetch<AboutTimelineItem[]>(`${adm}/about-timeline`),
  createAboutTimelineItem: (body: Omit<AboutTimelineItem, 'id' | 'is_active'>) =>
    cmsFetch<AboutTimelineItem>(`${adm}/about-timeline`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  updateAboutTimelineItem: (id: string, body: Partial<AboutTimelineItem>) =>
    cmsFetch<AboutTimelineItem>(`${adm}/about-timeline/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  deleteAboutTimelineItem: (id: string) =>
    cmsFetch<void>(`${adm}/about-timeline/${id}`, { method: 'DELETE' }),

  // Offices
  listOffices: () => cmsFetch<Office[]>(`${adm}/offices`),
  createOffice: (body: Omit<Office, 'id' | 'is_active'>) =>
    cmsFetch<Office>(`${adm}/offices`, { method: 'POST', body: JSON.stringify(body) }),
  updateOffice: (id: string, body: Partial<Office>) =>
    cmsFetch<Office>(`${adm}/offices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteOffice: (id: string) => cmsFetch<void>(`${adm}/offices/${id}`, { method: 'DELETE' }),
}
